import { faStore, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import usePagebuilder from '../../../../../hooks/pagebuilder/usePagebuilder';
import { useBooth } from '../../../../../hooks/useBooth';
import { useMobileApp } from '../../../../../hooks/useMobileApp';
import useMobileView from '../../../../../hooks/useMobileView';
import { useWindowSize } from '../../../../../hooks/useWindowSize';
import { Booth, EventInterface } from '../../../../../interfaces/interfaces';
import { BoothsComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { VyImage } from '../../../../common/VyImage';
import { VyUploadCrop } from '../../../../common/VyUploadCrop';
import InlineEditButton from '../../../pageBuilder/InlineEditButton';

interface SmallBoothProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  booth: Booth | undefined;
  event: EventInterface;
  uiComponent?: BoothsComponentInterface;
  spotlight?: boolean;
  showOnlineStatus?: boolean;
  previewOnly?: boolean;
}
const SmallBooth = ({ booth, event, uiComponent, spotlight = false, className, showOnlineStatus = false, previewOnly = false, ...props }: SmallBoothProps) => {
  const booths = useSelector((state) => state.common.booths);
  const user = useSelector((state) => state.common.user);

  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { width } = useWindowSize();
  const { uploadBackgroundImage, deleteBackground, hasEditPrivilege } = useBooth();
  const { t } = useTranslation();
  const { canEditBooth } = usePagebuilder();
  const { isMobileView, getMobileViewWidth } = useMobileView();
  const { isAppView } = useMobileApp();

  const showPlaceholder = uiComponent?.customStyling.showPlaceholder ?? true;
  const imageType = spotlight ? 'spotlightBackgroundImage' : 'backgroundImage';

  const _uploadBackgroundImage = async (image: Blob) => {
    setIsUpdating(true);
    try {
      await uploadBackgroundImage(image, imageType, booth?._id!);
    } catch (error) {
      console.error(error);
    }
    setIsUpdating(false);
  };

  const _deleteBackgroundImage = async () => {
    setIsUpdating(true);
    try {
      await deleteBackground(booth!, imageType);
    } catch (error) {
      console.error(error);
    }
    setIsUpdating(false);
  };

  const hasPrivilege = !!booth && !!user ? hasEditPrivilege(booth, user) : false;
  const fullwidthValue = getMobileViewWidth() ?? '100cqw';
  if (!booth) return <></>;

  const color = uiComponent?.customStyling.boothTextColor ?? event?.template?.mainButtonTextColor ?? uiComponent?.styling.textColor ?? 'white';
  const backgroundColor = uiComponent?.customStyling.boothBackgroundColor ?? event?.template?.mainColor;

  return (
    <div className={`w-full flex justify-center flex-grow group`}>
      <div
        className="flex flex-col w-full h-full transition-all duration-100 rounded-md hover:shadow-xl hover:bg-gray-100"
        style={{
          width: `${isMobileView() ? `calc(${fullwidthValue}*0.7)` : width && width > 1200 ? (spotlight ? '61rem' : `16rem`) : `100%`}`,
          color,
          backgroundColor,
        }}
        {...props}>
        <div className={`rounded-md relative duration-200 bg-cover h-48 ${className} ${isMobileView() ? 'w-full grid place-item-center' : ''}`}>
          {isEditMode && (
            <div
              className="absolute z-20 flex flex-row items-center w-full gap-3 p-3 text-white bg-gray-800 bg-opacity-50 rounded-t-md backdrop-blur-md"
              onClick={(e) => e.stopPropagation()}>
              <VyUploadCrop
                cropSize={{ width: spotlight ? 976 : 256, height: 192 }}
                onImage={_uploadBackgroundImage}
                className={`gray-bg cursor-pointer ${booth?.logo ? (spotlight ? 'w-[60%] @[1200px]:w-fit' : 'w-[60%]') : ''}`}>
                <div className="flex flex-row items-center gap-2 w-fit">
                  <span>{t('booth:upload-booth-preview')}</span>
                  <FontAwesomeIcon style={{ height: '14px', width: '14px', color: 'white' }} icon={faUpload} />
                </div>
              </VyUploadCrop>

              {booth[imageType] && (
                <label className="cursor-pointer gray-bg" onClick={() => _deleteBackgroundImage()}>
                  <FontAwesomeIcon style={{ height: '14px', width: '14px' }} className="text-white transition-colors hover:text-red-500" icon={faTrash} />
                </label>
              )}
            </div>
          )}
          {isUpdating && (
            <div className="absolute top-0 left-0 z-30 grid w-full h-full bg-black bg-opacity-50 place-items-center">
              <CircularProgress sx={{ color: 'lightgrey' }} />
            </div>
          )}

          {canEditBooth(booth) && !previewOnly && (
            <InlineEditButton
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              tooltip={isEditMode ? t('common:cancel') : t('pagebuilder:booths-edit-background')}
              forceShow={hasPrivilege}
              className="absolute z-30 p-2 text-white transition-opacity opacity-0 bottom-2 right-2 group-hover:opacity-100"
              stopPropagation={true}
            />
          )}
          {(spotlight && booth?.spotlightBackgroundImage) || (!spotlight && booth?.backgroundImage) ? (
            <VyImage
              id={spotlight && (isAppView() || (width && width > 1200)) ? booth?.spotlightBackgroundImage : booth?.backgroundImage}
              returnPlaceholder={false}
              className={`absolute w-full h-full object-cover rounded-t-md`}
              height={192}
              quality={70}
              style={{ zIndex: 0 }}
            />
          ) : (
            <div className="absolute grid w-full h-full text-gray-700 rounded-sm place-items-center">
              {showPlaceholder && <FontAwesomeIcon icon={faStore} color={color} className="w-14 h-14" />}
            </div>
          )}
          <div className="absolute z-10 w-full h-full text-black" />
          {booth?.logo && (
            <div className="absolute z-20 object-cover w-16 h-16 border rounded -top-1 -right-1">
              <VyImage className="rounded" id={booth.logo || ''} width={64} height={64} quality={80} alt={booth.name} />
            </div>
          )}
        </div>
        <div className="p-4 overflow-hidden font-bold break-normal text-md">
          <p style={{ color: uiComponent?.customStyling.boothTextColor }}>
            {event?.settings?.useBoothNumber && (
              <>
                <span>
                  {booth?.boothnumber ? `${booth?.boothnumber}` : ''}
                  <br />
                </span>
              </>
            )}
            {booth?.name}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmallBooth;
